import { useState, useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import type { AppEntry } from '../store/appsSlice'
import { updateApp } from '../store/appsSlice'
import { useAppDispatch } from '../store/hooks'
import './EditAppModal.css'

interface EditAppModalProps {
  app: AppEntry
  onClose: () => void
}

function EditAppModal({ app, onClose }: EditAppModalProps) {
  const dispatch = useAppDispatch()
  const [deploymentUrl, setDeploymentUrl] = useState(app.deployment_url ?? '')
  const [sourceUrl, setSourceUrl] = useState(app.source_url ?? '')
  const [createPullRequests, setCreatePullRequests] = useState(app.create_pull_requests ?? false)
  const [isPrivate, setIsPrivate] = useState(app.is_private ?? false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement
    modalRef.current?.focus()

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'
        )
        if (focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      previousFocusRef.current?.focus()
    }
  }, [onClose])

  const isValidUrl = (value: string): boolean => {
    try {
      const url = new URL(value)
      return url.protocol === 'http:' || url.protocol === 'https:'
    } catch {
      return false
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const trimmedDeploy = deploymentUrl.trim()
    const trimmedSource = sourceUrl.trim()

    if (trimmedDeploy && !isValidUrl(trimmedDeploy)) {
      setError('Deployment URL must be a valid http(s) URL.')
      return
    }
    if (trimmedSource && !isValidUrl(trimmedSource)) {
      setError('Source repository URL must be a valid http(s) URL.')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      await dispatch(updateApp({
        appId: app.id,
        deployment_url: trimmedDeploy || null,
        source_url: trimmedSource || null,
        create_pull_requests: createPullRequests,
        is_private: isPrivate,
      })).unwrap()
      onClose()
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to update app')
      setSubmitting(false)
    }
  }

  return (
    <div className="edit-modal-overlay" data-testid="edit-app-modal-overlay" onClick={onClose}>
      <div
        className="edit-modal"
        data-testid="edit-app-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-app-modal-title"
        ref={modalRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="edit-modal__header">
          <h2 className="edit-modal__title" id="edit-app-modal-title" data-testid="edit-app-modal-title">Edit App</h2>
          <button
            className="edit-modal__close"
            onClick={onClose}
            aria-label="Close dialog"
            data-testid="edit-app-modal-close"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        <p className="edit-modal__description">
          Update the deployed URL, source repository, pull-request behavior, or privacy for <strong>{app.name}</strong>.
        </p>
        <form className="edit-modal__form" onSubmit={handleSubmit} noValidate>
          <label className="edit-modal__label" htmlFor="edit-deployment-url">
            Deployment URL
          </label>
          <input
            id="edit-deployment-url"
            className="edit-modal__input"
            type="text"
            placeholder="https://my-app.netlify.app"
            value={deploymentUrl}
            onChange={(e) => setDeploymentUrl(e.target.value)}
            data-testid="edit-app-deployment-url"
            disabled={submitting}
          />
          <label className="edit-modal__label" htmlFor="edit-source-url">
            Source Repository URL
          </label>
          <input
            id="edit-source-url"
            className="edit-modal__input"
            type="text"
            placeholder="https://github.com/org/repo/tree/branch"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            data-testid="edit-app-source-url"
            disabled={submitting}
          />
          <p className="edit-modal__hint" data-testid="edit-app-source-hint">
            Use a GitHub <code>/tree/&lt;branch&gt;</code> URL. The repository and branch it points to
            become the clone &amp; push target for future builds of this app.
          </p>
          <label className="edit-modal__label" htmlFor="edit-secrets-branch">
            Secrets Branch
          </label>
          <input
            id="edit-secrets-branch"
            className="edit-modal__input"
            type="text"
            placeholder="Default push branch"
            value={app.secrets_branch ?? ''}
            data-testid="edit-app-secrets-branch"
            readOnly
          />
          <p className="edit-modal__hint" data-testid="edit-app-secrets-branch-hint">
            Branch whose Infisical secrets (<code>/branches/&lt;branch&gt;/</code>) future build
            containers for this app read. Blank uses the app's branch. Only the M2M
            API (<code>API_SERVICE_TOKEN</code>) can change it.
          </p>
          <div className="edit-modal__checkbox-row">
            <input
              id="edit-create-prs"
              className="edit-modal__checkbox"
              type="checkbox"
              checked={createPullRequests}
              onChange={(e) => setCreatePullRequests(e.target.checked)}
              data-testid="edit-app-create-prs"
              disabled={submitting}
            />
            <label className="edit-modal__checkbox-label" htmlFor="edit-create-prs">
              Open pull requests instead of pushing directly
            </label>
          </div>
          <p className="edit-modal__hint" data-testid="edit-app-create-prs-hint">
            When enabled, the New Chat options menu lets you specify a branch name (auto-generated
            if left blank). The chat's container commits to that branch and its first message is
            extended with instructions to open a pull request for it (if one doesn't already exist),
            instead of pushing directly.
          </p>
          <div className="edit-modal__checkbox-row">
            <input
              id="edit-is-private"
              className="edit-modal__checkbox"
              type="checkbox"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              data-testid="edit-app-is-private"
              disabled={submitting}
            />
            <label className="edit-modal__checkbox-label" htmlFor="edit-is-private">
              Make this app private
            </label>
          </div>
          <p className="edit-modal__hint" data-testid="edit-app-is-private-hint">
            Private apps are hidden from other users and only visible to owners.
          </p>
          {error && (
            <p className="edit-modal__error" role="alert" data-testid="edit-app-error">
              {error}
            </p>
          )}
          <div className="edit-modal__actions">
            <button
              type="button"
              className="edit-modal__btn edit-modal__btn--cancel"
              onClick={onClose}
              disabled={submitting}
              data-testid="edit-app-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="edit-modal__btn edit-modal__btn--submit"
              disabled={submitting}
              data-testid="edit-app-submit"
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditAppModal
